//-------TYPES--------//

export type TableOfContentsItem =
  | TableOfContentsDivider
  | TableOfContentsGroupItem

export type TableOfContentsDivider = {
  title: string
}

export type TableOfContentsGroupItem =
  | TableOfContentsGroup
  | TableOfContentsNodeGroup
  | TableOfContentsNode

export type TableOfContentsGroup = {
  title: string
  items: TableOfContentsGroupItem[]
}

export type TableOfContentsNode<T = 'overview' | 'http_service' | 'article'> = {
  id: string
  slug: string
  title: string
  type: T
  meta: string
  version?: string
}

export type TableOfContentsNodeGroup = TableOfContentsNode<'http_service'> &
  TableOfContentsGroup

//-------UTIL FUNCTIONS--------//

export function getHtmlIdFromItemId(id: string) {
  return `sl-toc-${id}`
}

export function isGroupOpenByDefault(
  depth: number,
  item: TableOfContentsGroup | TableOfContentsNodeGroup,
  activeId?: string,
  maxDepthOpenByDefault: number = 0
) {
  return (
    depth < maxDepthOpenByDefault ||
    (activeId &&
      (('id' in item && activeId === item.id) ||
        hasActiveItem(item.items, activeId)))
  )
}

// Recursively finds the first node
export function findFirstNode(
  items: TableOfContentsItem[]
): TableOfContentsNode | TableOfContentsNodeGroup | void {
  for (const item of items) {
    // ignore nodes with empty slug
    if ((isNode(item) || isNodeGroup(item)) && item.slug) {
      return item
    }

    if (isGroup(item) || isNodeGroup(item)) {
      const firstNode = findFirstNode(item.items)
      if (firstNode) {
        return firstNode
      }
    }

    continue
  }

  return
}

export function isDivider(
  item: TableOfContentsItem
): item is TableOfContentsDivider {
  return Object.keys(item).length === 1 && 'title' in item
}

export function isGroup(
  item: TableOfContentsItem
): item is TableOfContentsGroup {
  return Object.keys(item).length === 2 && 'title' in item && 'items' in item
}

export function isNodeGroup(
  item: TableOfContentsItem
): item is TableOfContentsNodeGroup {
  return (
    'title' in item &&
    'items' in item &&
    'slug' in item &&
    'id' in item &&
    'meta' in item &&
    'type' in item
  )
}

export function isNode(item: TableOfContentsItem): item is TableOfContentsNode {
  return (
    'title' in item &&
    'slug' in item &&
    'id' in item &&
    'meta' in item &&
    'type' in item
  )
}

// Recursively checks for the active item
function hasActiveItem(
  items: TableOfContentsGroupItem[],
  activeId: string
): boolean {
  return items.some((item) => {
    if ('id' in item && activeId === item.id) {
      return true
    }

    if ('items' in item) {
      return hasActiveItem(item.items, activeId)
    }

    return false
  })
}
